import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import StaffMessagesThread from '../../components/StaffMessagesThread';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { MessageSquare, Search, User } from 'lucide-react';
import { formatUK } from '../../lib/ukTime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMessagesPage() {
  const [staff, setStaff] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/staff-messages/overview`);
      setStaff(res.data.staff || []);
      setTotalUnread(res.data.total_unread || 0);
    } catch { /* empty state covers it */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useAutoRefresh(fetchOverview, { match: ['/messages'] });

  const openThread = (s) => setSelected(s);
  const closeThread = () => { setSelected(null); fetchOverview(); };

  const filtered = staff.filter((s) =>
    !search.trim() || (s.name || '').toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <AdminLayout>
      <div className="p-3 sm:p-6 space-y-5 min-h-screen">
        <div className="border-b border-border pb-4">
          <h1 className="text-xl sm:text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
            <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-primary" strokeWidth={1.5} />
            MESSAGES
            {totalUnread > 0 && (
              <Badge className="bg-primary text-primary-foreground rounded-full text-xs" data-testid="messages-total-unread">
                {totalUnread} new
              </Badge>
            )}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Direct messages with staff. Unread conversations appear first.
          </p>
        </div>

        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search staff by name..."
            className="pl-9 rounded-sm"
            data-testid="messages-search-input"
          />
        </div>

        {loading ? (
          <div className="py-10 text-center animate-pulse text-muted-foreground">Loading...</div>
        ) : (
          <div className="divide-y divide-border border border-border rounded-sm bg-card" data-testid="messages-staff-list">
            {filtered.length === 0 && (
              <p className="p-6 text-sm text-muted-foreground text-center">No staff found.</p>
            )}
            {filtered.map((s) => (
              <button
                key={s.id}
                onClick={() => openThread(s)}
                className="w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors flex items-center gap-3"
                data-testid={`messages-row-${s.id}`}
              >
                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm truncate ${s.unread ? 'font-bold text-foreground' : ''}`}>{s.name}</span>
                    {s.role === 'supervisor' && (
                      <Badge className="text-[9px] uppercase bg-blue-500/15 text-blue-300 border-blue-500/30 rounded-sm">Sec. Admin</Badge>
                    )}
                    {s.unread > 0 && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold text-primary-foreground bg-primary rounded-full px-2 py-0.5"
                        data-testid={`messages-unread-${s.id}`}
                      >
                        <MessageSquare className="w-3 h-3" /> {s.unread}
                      </span>
                    )}
                  </div>
                  <p className={`text-xs truncate mt-0.5 ${s.unread ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {s.last_at
                      ? `${s.last_sender === 'admin' ? 'You: ' : ''}${s.last_body}`
                      : 'No messages yet'}
                  </p>
                </div>
                {s.last_at && (
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                    {formatUK(s.last_at, 'd MMM HH:mm')}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) closeThread(); }}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="messages-thread-sheet">
          <SheetHeader>
            <SheetTitle className="font-barlow tracking-widest uppercase flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-primary" /> {selected?.name}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            {selected && <StaffMessagesThread viewer="admin" employeeId={selected.id} />}
          </div>
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
}
